import { motion } from 'framer-motion'
import { ArrowRight, CheckCircle2, Phone, Leaf, Sparkles } from 'lucide-react'
import { servicesData, companyInfo } from '../data/data'
import { Link } from 'react-router-dom'
import { getIcon } from '../utils/iconMap'

const stats = [
    { value: '7', label: 'Services spécialisés' },
    { value: '20+', label: 'Agriculteurs accompagnés' },
    { value: '15+', label: "Années d'expérience" },
    { value: '100%', label: 'Savoir-faire local' },
]

const process = [
    { step: '01', title: 'Premier Contact', description: 'Échange initial pour comprendre vos besoins' },
    { step: '02', title: 'Diagnostic', description: 'Analyse approfondie de votre situation' },
    { step: '03', title: "Plan d'Action", description: 'Élaboration d\'une stratégie personnalisée' },
    { step: '04', title: 'Suivi & Support', description: 'Accompagnement continu et ajustements' },
]

const Services = () => {
    const heroImage = servicesData[0]?.image

    return (
        <div className="min-h-screen bg-neutral-light">
            {/* ===== Hero ===== */}
            <section className="relative overflow-hidden py-24 lg:py-32">
                {/* Background image + overlays */}
                <div className="absolute inset-0">
                    <img
                        src={heroImage}
                        alt="Exploitation agropastorale SaDEAP"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-green/95 via-primary-green/85 to-primary-green/60" />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-green/90 via-transparent to-transparent" />
                </div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-yellow/20 rounded-full blur-3xl" />
                <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-primary-light/20 rounded-full blur-3xl" />

                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl text-white"
                    >
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-custom border border-white/25 rounded-full text-sm font-semibold mb-6">
                            <Sparkles className="w-4 h-4 text-primary-yellow" />
                            NOS SERVICES
                        </span>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-shadow-lg leading-tight">
                            Un accompagnement complet, <span className="text-primary-yellow">du champ au marché</span>
                        </h1>
                        <p className="text-xl text-white/90 mb-8 max-w-2xl">
                            De la gestion de ferme à la transformation, nous mettons notre savoir-faire de
                            producteur au service de votre réussite agricole.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link to="/contact" className="btn-secondary">
                                Demander un devis gratuit
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Link>
                            <a
                                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                                className="btn-outline !text-white !border-white hover:!bg-white hover:!text-primary-green"
                            >
                                <Phone className="mr-2 w-5 h-5" />
                                Nous appeler
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ===== Stats ===== */}
            <section className="relative z-20 -mt-12">
                <div className="container-custom">
                    <div className="bg-white rounded-3xl shadow-xl p-8 md:p-10">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-gray-100">
                            {stats.map((stat, i) => (
                                <motion.div
                                    key={stat.label}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                                    className="pt-6 md:pt-0"
                                >
                                    <div className="text-4xl lg:text-5xl font-bold text-primary-green mb-1">
                                        {stat.value}
                                    </div>
                                    <p className="text-gray-600 text-sm">{stat.label}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== Services Details ===== */}
            <section className="section-padding">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-16 max-w-2xl mx-auto"
                    >
                        <span className="inline-block px-4 py-2 bg-primary-yellow/20 text-primary-green rounded-full text-sm font-semibold mb-4">
                            CE QUE NOUS FAISONS
                        </span>
                        <h2 className="text-3xl lg:text-4xl font-bold text-primary-green mb-4">
                            Des solutions concrètes pour chaque étape
                        </h2>
                        <p className="text-lg text-gray-600">
                            Sept services pensés autour de notre expérience réelle de production, d'élevage
                            et de transformation.
                        </p>
                    </motion.div>

                    <div className="space-y-20 lg:space-y-28">
                        {servicesData.map((service, index) => {
                            const ServiceIcon = getIcon(service.icon)
                            const reversed = index % 2 === 1
                            return (
                                <motion.div
                                    key={service.id}
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.7 }}
                                    viewport={{ once: true, margin: '-80px' }}
                                    className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
                                >
                                    {/* Image */}
                                    <div className={`relative ${reversed ? 'lg:order-2' : ''}`}>
                                        <div className="relative rounded-[2rem] overflow-hidden shadow-2xl group">
                                            <img
                                                src={service.image}
                                                alt={service.title}
                                                loading="lazy"
                                                className="w-full h-80 sm:h-[26rem] object-cover transition-transform duration-700 group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                                            {/* Icon badge */}
                                            <div className="absolute top-6 left-6 w-16 h-16 rounded-2xl bg-white/95 backdrop-blur-custom flex items-center justify-center shadow-lg">
                                                <ServiceIcon className="w-8 h-8 text-primary-green" />
                                            </div>

                                            {/* Number */}
                                            <span className="absolute top-4 right-6 text-7xl font-bold text-white/25 leading-none select-none">
                                                {String(service.id).padStart(2, '0')}
                                            </span>

                                            {/* Tagline */}
                                            <div className="absolute bottom-6 left-6 right-6">
                                                <p className="text-white text-lg font-semibold flex items-center gap-2">
                                                    <Leaf className="w-5 h-5 text-primary-yellow flex-shrink-0" />
                                                    {service.tagline}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className={reversed ? 'lg:order-1' : ''}>
                                        <span className="inline-block px-4 py-1.5 bg-primary-yellow/20 text-primary-green rounded-full text-xs font-semibold tracking-wide mb-4">
                                            SERVICE {String(service.id).padStart(2, '0')}
                                        </span>
                                        <h3 className="text-3xl lg:text-4xl font-bold text-primary-green mb-5">
                                            {service.title}
                                        </h3>
                                        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                            {service.description}
                                        </p>

                                        {/* Features */}
                                        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-8">
                                            {service.features.map((feature, idx) => (
                                                <motion.div
                                                    key={idx}
                                                    initial={{ opacity: 0, x: -15 }}
                                                    whileInView={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: idx * 0.08 }}
                                                    viewport={{ once: true }}
                                                    className="flex items-start"
                                                >
                                                    <CheckCircle2 className="text-primary-light w-5 h-5 mt-0.5 mr-2.5 flex-shrink-0" />
                                                    <span className="text-gray-700">{feature}</span>
                                                </motion.div>
                                            ))}
                                        </div>

                                        {/* Highlights pills */}
                                        {service.highlights && (
                                            <div className="flex flex-wrap gap-2 mb-8">
                                                {service.highlights.map((h) => (
                                                    <span
                                                        key={h}
                                                        className="inline-flex items-center px-3 py-1.5 bg-primary-green/5 text-primary-green text-sm rounded-lg border border-primary-green/10"
                                                    >
                                                        <span className="w-1.5 h-1.5 bg-primary-light rounded-full mr-2" />
                                                        {h}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {/* CTA */}
                                        <div className="flex flex-col sm:flex-row gap-4">
                                            <Link to="/contact" className="btn-primary group">
                                                Demander ce service
                                                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                            </Link>
                                            <a
                                                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                                                className="btn-outline group"
                                            >
                                                <Phone className="mr-2 w-5 h-5" />
                                                Appeler
                                            </a>
                                        </div>
                                    </div>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* ===== Process ===== */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-14"
                    >
                        <span className="inline-block px-4 py-2 bg-primary-yellow/20 text-primary-green rounded-full text-sm font-semibold mb-4">
                            NOTRE PROCESSUS
                        </span>
                        <h2 className="text-3xl lg:text-4xl font-bold text-primary-green mb-4">
                            Comment nous travaillons
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Un processus simple et efficace pour vous accompagner
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-4 gap-8">
                        {process.map((item, index) => (
                            <motion.div
                                key={item.step}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="relative text-center"
                            >
                                <div className="w-20 h-20 bg-primary-green text-white rounded-2xl flex items-center justify-center mx-auto mb-5 text-2xl font-bold shadow-lg">
                                    {item.step}
                                </div>
                                <h3 className="text-xl font-bold text-primary-green mb-2">{item.title}</h3>
                                <p className="text-gray-600">{item.description}</p>
                                {index < process.length - 1 && (
                                    <div className="hidden md:block absolute top-10 left-[62%] w-3/4 h-0.5 bg-gradient-to-r from-primary-green/30 to-transparent" />
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== Pricing ===== */}
            <section className="section-padding">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-14"
                    >
                        <span className="inline-block px-4 py-2 bg-primary-yellow/20 text-primary-green rounded-full text-sm font-semibold mb-4">
                            TARIFICATION
                        </span>
                        <h2 className="text-3xl lg:text-4xl font-bold text-primary-green mb-4">
                            Des formules adaptées à vos besoins
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Choisissez la formule qui correspond le mieux à votre activité
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-8 items-start">
                        {[
                            {
                                name: 'Essentiel',
                                price: '50.000',
                                period: 'FCFA/mois',
                                features: [
                                    'Conseil de base',
                                    '1 visite mensuelle',
                                    'Support par téléphone',
                                    'Accès aux formations',
                                ],
                                recommended: false,
                            },
                            {
                                name: 'Professionnel',
                                price: '100.000',
                                period: 'FCFA/mois',
                                features: [
                                    'Conseil approfondi',
                                    '2 visites mensuelles',
                                    'Support prioritaire',
                                    'Formations avancées',
                                    'Location de matériel -20%',
                                ],
                                recommended: true,
                            },
                            {
                                name: 'Premium',
                                price: 'Sur devis',
                                period: '',
                                features: [
                                    'Accompagnement complet',
                                    'Visites illimitées',
                                    'Support 24/7',
                                    'Toutes les formations',
                                    'Location de matériel -40%',
                                    'Partenariat commercial',
                                ],
                                recommended: false,
                            },
                        ].map((plan, index) => (
                            <motion.div
                                key={plan.name}
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -8 }}
                                className={`relative ${plan.recommended ? 'md:-mt-4' : ''}`}
                            >
                                {plan.recommended && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                                        <span className="bg-primary-yellow text-primary-green px-4 py-1 rounded-full text-sm font-semibold shadow-md">
                                            Recommandé
                                        </span>
                                    </div>
                                )}
                                <div
                                    className={`bg-white rounded-2xl shadow-xl p-8 h-full ${
                                        plan.recommended ? 'border-2 border-primary-green ring-4 ring-primary-green/5' : ''
                                    }`}
                                >
                                    <h3 className="text-2xl font-bold text-primary-green mb-4">{plan.name}</h3>
                                    <div className="mb-6">
                                        <span className="text-4xl font-bold text-primary-green">{plan.price}</span>
                                        {plan.period && <span className="text-gray-500 ml-1 text-sm">{plan.period}</span>}
                                    </div>
                                    <ul className="space-y-3 mb-8">
                                        {plan.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-center">
                                                <CheckCircle2 className="text-primary-light mr-3 w-5 h-5 flex-shrink-0" />
                                                <span className="text-gray-700">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Link
                                        to="/contact"
                                        className={plan.recommended ? 'btn-primary w-full' : 'btn-outline w-full'}
                                    >
                                        Choisir cette formule
                                    </Link>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== CTA ===== */}
            <section className="section-padding bg-primary-green relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-yellow/20 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-light/20 rounded-full blur-3xl" />
                <div className="container-custom relative z-10">
                    <div className="text-center text-white max-w-2xl mx-auto">
                        <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                            Prêt à booster votre production ?
                        </h2>
                        <p className="text-xl text-white/90 mb-8">
                            Nos équipes sont là pour vous accompagner dans votre développement agricole.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/contact" className="btn-secondary">
                                Obtenir un devis gratuit
                            </Link>
                            <a
                                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                                className="btn-outline !text-white !border-white hover:!bg-white hover:!text-primary-green"
                            >
                                <Phone className="mr-2 w-5 h-5" />
                                Appelez-nous
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default Services
